import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import styles from "./AmberPaywall.module.scss"

/**
 * AmberPaywall (`/amber`) — a self-contained ONBOARDING paywall for Private
 * Photo Vault, staged as a single warm GEMSTONE of AMBER on ivory. Real amber
 * (fossilised tree resin) holds whatever it captures — a leaf, an insect —
 * perfectly, in full detail, for millions of years. That is the honest metaphor
 * for the app: your memories, kept exactly as you captured them — full-resolution
 * originals, private, and safe on every device — for good.
 *
 * Divergence brief: claude-paywalls/_ledger.md · concept doc: claude-paywalls/amber.md
 *
 * Mechanic (new to the ledger): RAISE THE RESIN / SET-IN-AMBER. A cluster of your
 * first memories floats loose inside the gem — faintly drifting, dashed "not yet
 * kept" outlines. Drag the amber level up (grab the gem, drag the rim bead, tap a
 * loose memory, press "Set them in amber", or use the keyboard) and the warm resin
 * rises; every memory the meniscus passes stops dead-still and is encased — gaining
 * a glassy amber facet and a brand-blue "kept" ring. STILLNESS is the argument:
 * loose memories jitter (at risk); kept ones are held perfectly still forever. The
 * photos stay full-colour throughout — this is encasement, deliberately NOT a
 * grey→colour reveal.
 *
 * Onboarding framing (honest, behavioural): ENDOWED PROGRESS + GOAL-GRADIENT +
 * FRESH-START. The gem arrives with your first two memories already set ("we kept
 * these to start you off"); a live "n/7 kept" meter invites you to finish. The
 * register is warm / precious / permanence — not the cosmic-awe (Aurora), deep-calm
 * (Abyss), steel-tactility (Vault Door) or future-self scrub (Longview) of the four
 * prior onboarding paywalls.
 *
 * Every ledger axis is chosen to differ:
 *   - Layout:      a single organic AMBER CABOCHON as the stage with memories
 *                  suspended inside + a rising resin meniscus + a separate plan
 *                  module below — not a centered hero card, canvas descent, crank
 *                  door or time-scrubber tower.
 *   - Interaction: RAISE-THE-RESIN to encase-and-still — distinct from every prior
 *                  scrub / hold / flip / toss / paint / dial / riffle / node-graph /
 *                  draw-a-link / lever / stitch / lasso / drag-a-piece / rope-haul /
 *                  key-wind.
 *   - Register:    warm / precious / wondrous PERMANENCE.
 *   - Art:         LIGHT ivory + honey-gold GEM (brand amber pushed to resin);
 *                  brand blue = the KEPT / encased state (kept rings, ticks,
 *                  featured yearly, CTA). Photos always full-colour. Breaks the
 *                  dark/skeuomorphic onboarding cluster.
 *   - Value:      PRESERVATION / keeping originals exactly — full quality, private,
 *                  backed up, forever — reached for a brand-new user.
 *
 * Honest & App Store compliant: amber only keeps memories safe, it never holds them
 * hostage and nothing is ever deleted (copy says so). Real Premium features only
 * (full-res originals, encrypted, backed up + in sync on every device). Yearly is
 * the featured, default plan and carries the most prominent price; monthly is small
 * and secondary. Save % is exact yearly-vs-monthly arithmetic; no fake countdown or
 * scarcity. Purchase is never gated on the demo. Memory tiles are bundled demo
 * photos from `public/byheart/` (no network egress).
 */

type PlanKey = "yearly" | "monthly"

// your first memories, suspended in the gem (bundled demo photos)
// threshold = the resin level at which this memory is encased; lower sits lower in
// the gem, so the rising meniscus reaches it first. left/top place it organically.
const MEMORIES = [
  { src: "m03.jpg", thr: 0.14, left: 42, top: 66, size: 62, rot: -7 },
  { src: "m07.jpg", thr: 0.28, left: 19, top: 58, size: 54, rot: 6 },
  { src: "m02.jpg", thr: 0.42, left: 62, top: 51, size: 58, rot: 8 },
  { src: "m05.jpg", thr: 0.55, left: 34, top: 43, size: 60, rot: -5 },
  { src: "m01.jpg", thr: 0.68, left: 16, top: 36, size: 46, rot: -9 },
  { src: "m06.jpg", thr: 0.8, left: 54, top: 30, size: 52, rot: 5 },
  { src: "m04.jpg", thr: 0.92, left: 35, top: 24, size: 50, rot: -4 },
].map((m) => ({ ...m, src: `/byheart/${m.src}` }))

const START_FILL = 0.34 // endowed: the two lowest memories arrive already kept
const clamp = (v: number, lo: number, hi: number) =>
  Math.max(lo, Math.min(hi, v))

// ── tiny inline icons ─────────────────────────────────────────────────────────
const Tick = ({ size = 11 }: { size?: number }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden>
    <path
      d="M5 12.5l4.2 4.2L19 7"
      fill="none"
      stroke="currentColor"
      strokeWidth={3}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)
const Lock = ({ size = 13 }: { size?: number }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden>
    <rect
      x={4.5}
      y={10.5}
      width={15}
      height={10}
      rx={2.6}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
    />
    <path
      d="M7.5 10.5V8a4.5 4.5 0 0 1 9 0v2.5"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
    />
  </svg>
)
const Gem = ({ size = 13 }: { size?: number }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden>
    <path
      d="M6 3h12l3 5-9 13L3 8z"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinejoin="round"
    />
    <path
      d="M3 8h18M9 3L6 8l6 13M15 3l3 5-6 13"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.2}
      strokeLinejoin="round"
      opacity={0.6}
    />
  </svg>
)

const AmberPaywall = () => {
  const { state } = useAppState()
  const reduceMotion = useReducedMotion()

  // ── pricing (real debug data; yearly featured) ──────────────────────────────
  const premium = state.productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPrice = yearlyProduct
    ? formatCurrency(yearlyProduct.price, locale)
    : "$35.99"
  const yearlyPerMonth = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 12, locale)
    : "$3.00"
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyProduct.price, locale)
    : "$13.99"
  const hasTrial = yearlyProduct?.hasTrial ?? true
  const trialDays = yearlyProduct?.introPricing?.period ?? 7

  const savingsPct = useMemo(() => {
    const y = yearlyProduct?.price ?? 35.99
    const m = monthlyProduct?.price ?? 13.99
    if (!m) return 0
    return Math.round((1 - y / (m * 12)) * 100)
  }, [yearlyProduct, monthlyProduct])

  const [plan, setPlan] = useState<PlanKey>("yearly")

  // ── the resin level: single source of truth ─────────────────────────────────
  const [fill, setFill] = useState(START_FILL) // 0 (all loose) → 1 (all set)
  const [handed, setHanded] = useState(false) // auto-demo done / user has it

  const fillRef = useRef(START_FILL)
  const draggingRef = useRef(false)
  const lastYRef = useRef(0)
  const rampRef = useRef<number | null>(null)
  const demoRef = useRef<number | null>(null)
  const stageRef = useRef<HTMLDivElement | null>(null)

  const setFillTo = (v: number) => {
    const c = clamp(v, 0, 1)
    fillRef.current = c
    setFill(c)
  }

  const keptCount = MEMORIES.filter((m) => fill >= m.thr).length
  const lit = keptCount === MEMORIES.length

  const stopRamp = () => {
    if (rampRef.current) cancelAnimationFrame(rampRef.current)
    rampRef.current = null
  }
  const stopDemo = () => {
    if (demoRef.current) window.clearTimeout(demoRef.current)
    demoRef.current = null
  }

  // ramp the resin to a target (used by demo, "Set them in amber" + keyboard)
  const rampFillTo = useCallback((target: number, ms: number) => {
    stopRamp()
    const from = fillRef.current
    let start: number | null = null
    const tick = (t: number) => {
      if (start === null) start = t
      const k = clamp((t - start) / ms, 0, 1)
      const eased = 1 - Math.pow(1 - k, 3)
      setFillTo(from + (target - from) * eased)
      if (k < 1) rampRef.current = requestAnimationFrame(tick)
      else rampRef.current = null
    }
    rampRef.current = requestAnimationFrame(tick)
  }, [])

  // ── the teaching pour: rise a touch to set a third, then hand it over ────────
  useEffect(() => {
    if (reduceMotion) {
      setFillTo(1)
      setHanded(true)
      return
    }
    demoRef.current = window.setTimeout(() => {
      rampFillTo(0.5, 1050)
      demoRef.current = window.setTimeout(() => setHanded(true), 1250)
    }, 750)
    return () => {
      stopDemo()
      stopRamp()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reduceMotion])

  const beginInteract = () => {
    stopDemo()
    stopRamp()
    setHanded(true)
  }

  // ── pointer: drag anywhere on the gem up/down to raise/lower the resin ────────
  const onDown = (e: React.PointerEvent) => {
    beginInteract()
    draggingRef.current = true
    lastYRef.current = e.clientY
    ;(e.currentTarget as Element).setPointerCapture?.(e.pointerId)
  }
  const onMove = (e: React.PointerEvent) => {
    if (!draggingRef.current) return
    const dy = e.clientY - lastYRef.current
    lastYRef.current = e.clientY
    const h = stageRef.current?.getBoundingClientRect().height || 300
    const before = fillRef.current
    setFillTo(before - dy / (h * 0.72)) // drag up = fill up
    if (before < 0.92 && fillRef.current >= 0.92) {
      native.logMixpanelEvent("html_amber_all_set", {})
    }
  }
  const onUp = (e: React.PointerEvent) => {
    if (!draggingRef.current) return
    draggingRef.current = false
    ;(e.currentTarget as Element).releasePointerCapture?.(e.pointerId)
  }

  // tap a single loose memory → raise the resin just enough to keep it
  const keepOne = (thr: number) => {
    beginInteract()
    if (fillRef.current >= thr) return
    rampFillTo(Math.min(thr + 0.02, 1), 460)
    native.logMixpanelEvent("html_amber_keep_one", {})
  }

  const setAll = () => {
    beginInteract()
    rampFillTo(1, 950)
    native.logMixpanelEvent("html_amber_set_all", {})
  }
  const loosen = () => {
    beginInteract()
    rampFillTo(0, 700)
    native.logMixpanelEvent("html_amber_reset", {})
  }
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault()
      beginInteract()
      rampFillTo(clamp(fillRef.current + 0.2, 0, 1), 420)
    } else if (e.key === "ArrowUp") {
      e.preventDefault()
      beginInteract()
      setFillTo(fillRef.current + 0.06)
    } else if (e.key === "ArrowDown") {
      e.preventDefault()
      beginInteract()
      setFillTo(fillRef.current - 0.06)
    }
  }

  // ── plan + purchase wiring ────────────────────────────────────────────────────
  const choosePlan = (key: PlanKey) => {
    setPlan(key)
    native.logMixpanelEvent("html_pricing_toggle", { plan: key })
  }
  const handleClose = () => native.postNativeEvent("close")
  const purchase = () => {
    const product = plan === "yearly" ? yearlyProduct : monthlyProduct
    if (product) native.subscribe(product)
  }

  // ── copy ───────────────────────────────────────────────────────────────────
  const headline = lit
    ? "Kept in amber — for good."
    : "Keep them exactly as they are."
  const sub = lit
    ? "Full-resolution originals, private and encrypted, backed up and in sync on every device — held exactly as you captured them."
    : "Real amber keeps a moment perfectly for millions of years. Private Photo Vault keeps yours the same way — every original, in full quality, safe and private."
  const hint = !handed
    ? "Setting your first memories…"
    : lit
      ? "Every memory is set — kept perfectly still, in full quality, forever. Start with Yearly to keep it that way."
      : keptCount > MEMORIES.filter((m) => START_FILL >= m.thr).length
        ? `${keptCount} of ${MEMORIES.length} kept — raise the amber to set the rest.`
        : "Your first two are already kept. Drag the amber up to set the rest in place."

  const ctaLabel =
    plan === "yearly"
      ? hasTrial
        ? `Start ${trialDays} days free`
        : "Keep everything — Yearly"
      : "Choose monthly"
  const ctaSub =
    plan === "yearly"
      ? hasTrial
        ? `${trialDays} days free, then ${yearlyPrice}/yr · cancel anytime`
        : `${yearlyPrice}/yr · auto-renews · cancel anytime`
      : `${monthlyPrice}/mo · auto-renews · cancel anytime`

  const stageVars = {
    ["--fill" as any]: fill.toFixed(3),
    ["--pct" as any]: Math.round(fill * 100),
  } as React.CSSProperties

  return (
    <div className={styles.page}>
      <button className={styles.close} onClick={handleClose} aria-label="Close">
        <svg viewBox="0 0 24 24" width={16} height={16} aria-hidden>
          <path
            d="M6 6l12 12M18 6L6 18"
            stroke="currentColor"
            strokeWidth={2.2}
            strokeLinecap="round"
          />
        </svg>
      </button>

      <header className={styles.head}>
        <div className={styles.brand}>
          <span className={styles.brandKey} aria-hidden>
            <Lock size={12} />
          </span>
          <span>Private Photo Vault</span>
          <span className={styles.premiumPip}>Premium</span>
        </div>
        <h1 className={`${styles.title} ${lit ? styles.titleLit : ""}`}>
          {headline}
        </h1>
        <p className={styles.sub}>{sub}</p>
      </header>

      {/* ── the amber gem — memories suspended inside ─────────────────────────── */}
      <div
        className={`${styles.stage} ${lit ? styles.stageLit : ""} ${
          reduceMotion ? styles.stageStill : ""
        }`}
        ref={stageRef}
        style={stageVars}
        role="group"
        tabIndex={0}
        aria-label="A gem of amber holding your first memories — drag it up to set them in place"
        onKeyDown={onKeyDown}
        onPointerDown={onDown}
        onPointerMove={onMove}
        onPointerUp={onUp}
        onPointerCancel={onUp}
      >
        <span className={styles.halo} aria-hidden />

        <div className={styles.gem}>
          {/* the rising resin body + its bright meniscus */}
          <span className={styles.resin} aria-hidden>
            <span className={styles.meniscus} />
          </span>
          {/* facets / gloss on the gem surface */}
          <span className={styles.facetA} aria-hidden />
          <span className={styles.facetB} aria-hidden />
          <span className={styles.sheen} aria-hidden />
          <span className={styles.sweep} aria-hidden />

          {/* the memories suspended in the resin */}
          {MEMORIES.map((m, i) => {
            const kept = fill >= m.thr
            return (
              <button
                key={m.src}
                className={`${styles.mote} ${kept ? styles.moteKept : styles.moteLoose}`}
                style={
                  {
                    left: `${m.left}%`,
                    top: `${m.top}%`,
                    ["--sz" as any]: `${m.size}px`,
                    ["--rot" as any]: `${m.rot}deg`,
                    ["--i" as any]: i,
                  } as React.CSSProperties
                }
                onClick={(e) => {
                  e.stopPropagation()
                  keepOne(m.thr)
                }}
                aria-label={kept ? "Kept in amber" : "Tap to keep this memory"}
              >
                <img
                  src={m.src}
                  alt=""
                  draggable={false}
                  onError={(e) =>
                    ((e.currentTarget as HTMLImageElement).style.opacity = "0")
                  }
                />
                <span className={styles.moteFacet} aria-hidden />
                <span className={styles.moteRing} aria-hidden />
                <span className={styles.moteTick} aria-hidden>
                  <Tick size={9} />
                </span>
              </button>
            )
          })}
        </div>

        {/* status fittings: kept meter + quality plate */}
        <span className={styles.keptTag} aria-hidden>
          <span className={styles.keptDot} />
          {lit ? "ALL KEPT" : `KEPT ${keptCount}/${MEMORIES.length}`}
        </span>
        <span className={styles.plate} aria-hidden>
          <span className={styles.plateGem}>
            <Gem size={10} />
          </span>
          FULL QUALITY · FOREVER
        </span>
      </div>

      <p
        className={`${styles.hintLine} ${lit ? styles.hintLit : ""}`}
        aria-live="polite"
      >
        {hint}
      </p>

      <div className={styles.tools}>
        <button className={styles.toolPrimary} onClick={setAll} disabled={lit}>
          <Gem size={13} />
          {lit ? "All set in amber" : "Set them in amber"}
        </button>
        <button
          className={styles.toolGhost}
          onClick={loosen}
          disabled={fill <= START_FILL + 0.001}
        >
          Start over
        </button>
      </div>

      {/* what "kept in amber" means — the honest Premium bundle */}
      <ul className={styles.keeps}>
        {[
          "Every original in full quality — kept exactly, never compressed",
          "Private & encrypted — only your passcode opens the vault",
          "Backed up & in sync on every device — automatically",
        ].map((t) => (
          <li key={t}>
            <span className={styles.keepDot} aria-hidden>
              <Tick size={10} />
            </span>
            {t}
          </li>
        ))}
      </ul>

      {/* ── plans — yearly featured, monthly small & secondary ─────────────────── */}
      <div className={styles.plans}>
        <button
          className={`${styles.planYear} ${
            plan === "yearly" ? styles.planSel : ""
          }`}
          onClick={() => choosePlan("yearly")}
          aria-pressed={plan === "yearly"}
        >
          <span className={styles.planMain}>
            <span className={styles.planName}>Yearly</span>
            <span className={styles.planNote}>Keep everything, all year</span>
          </span>
          <span className={styles.planPrice}>
            <span className={styles.planBig}>{yearlyPrice}</span>
            <span className={styles.planPer}>/yr</span>
            <span className={styles.planSmall}>
              {yearlyPerMonth}/mo
              {savingsPct > 0 ? ` · save ${savingsPct}%` : ""}
            </span>
          </span>
          {savingsPct > 0 && (
            <span className={styles.saveBadge}>SAVE {savingsPct}%</span>
          )}
        </button>

        <button
          className={`${styles.planMonth} ${
            plan === "monthly" ? styles.planMonthSel : ""
          }`}
          onClick={() => choosePlan("monthly")}
          aria-pressed={plan === "monthly"}
        >
          <span className={styles.planMonthName}>Monthly</span>
          <span className={styles.planMonthPrice}>{monthlyPrice}/mo</span>
        </button>
      </div>

      <button className={styles.cta} onClick={purchase}>
        <span className={styles.ctaLabel}>{ctaLabel}</span>
        <span className={styles.ctaSub}>{ctaSub}</span>
      </button>

      <p className={styles.reassure}>
        Amber only keeps your memories safe — it never holds them hostage. Your
        photos are always yours, and nothing is ever deleted.
      </p>

      <div className={styles.legal}>
        <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
        <span aria-hidden>·</span>
        <button onClick={() => native.postNativeEvent("privacy")}>
          Privacy
        </button>
        <span aria-hidden>·</span>
        <button onClick={() => native.postNativeEvent("already_purchased")}>
          Restore
        </button>
      </div>
    </div>
  )
}

export default AmberPaywall
